import React from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { ProductInformation } from "shared/types/__generated__/graphql"
import { AnchorProps } from "shared/ui/link/Anchor"
import { Readonly } from "shared/ui/readonly/Readonly"

import { ProductPositionTitle } from "entities/product-positions/ui/product-position-title/ProductPositionTitle"
import { getProductType } from "entities/products/lib/getProductType"
import { ProductPositionFormModel } from "features/product-position-management/types/ProductPositionFormModel"
import { ProjectPageSidebarActions } from "pages/project-page/ui/ProjectPageSidebarActions"
import { ProductPositionForm } from "widgets/product-position/product-position-form/ProductPositionForm"

import "../../styles/_project-page-sidebar.scss"
import { ProductPositionSidebarGeneralTab } from "./ProductPositionSidebarGeneralTab"
import { ProjectPageProductPositionSidebarProvider } from "./ProjectPageProductPositionSidebarContext"

export interface ProjectPageProductPositionSidebarViewModel {
  image?: React.ReactNode

  isLoading?: boolean
  isLoadingTitle?: boolean

  productInformation?: ProductInformation | undefined | null
  isLoadingProductInformation?: boolean

  titleLinkProps?: AnchorProps

  autoFocus?: boolean

  formModel: ProductPositionFormModel

  actions: Action[]
  hideControls?: boolean

  isDisabled?: boolean
  isReadonly?: boolean
}

export const ProjectPageProductPositionSidebarView = React.memo(
  (viewModel: ProjectPageProductPositionSidebarViewModel) => {
    const { t } = useTranslation()

    const {
      titleLinkProps,
      image,
      productInformation,
      isLoadingProductInformation,
      actions,
      autoFocus,
      hideControls,
      isLoading,
      formModel,
      isDisabled,
      isReadonly
    } = viewModel

    const title =
      productInformation?.title ??
      (formModel.productPosition?.variantKey
        ? getProductType(formModel.productPosition.variantKey)
        : null)

    return (
      <section
        aria-label={t("Project.SelectedProductPositionSidebar")}
        className="project-page-sidebar project-page-sidebar--product-position"
        data-testid="project-sidebar"
      >
        <div className="project-page-sidebar__content">
          <ProjectPageProductPositionSidebarProvider
            value={{ image, titleLinkProps }}
          >
            <ProductPositionForm
              {...formModel}
              autoFocus
              isLoading={isLoading}
              isDisabled={isDisabled}
              productInformation={productInformation}
              isLoadingProductInformation={isLoadingProductInformation}
            >
              <Readonly.Root
                className="project-page-sidebar__title"
                isLoading={isLoadingProductInformation}
              >
                <Readonly.Label>{t("Product.Product")}</Readonly.Label>
                <Readonly.Value isLoading={isLoadingProductInformation}>
                  {title ? (
                    <ProductPositionTitle
                      title={title}
                      hasClarificationRequest={
                        formModel.productPosition?.hasClarificationRequest
                      }
                      showClarificationRequestIndicator
                    />
                  ) : null}
                </Readonly.Value>
              </Readonly.Root>
              <ProductPositionForm.Tabs
                generalTabComponent={ProductPositionSidebarGeneralTab}
              />
            </ProductPositionForm>
          </ProjectPageProductPositionSidebarProvider>
        </div>
        {!hideControls && !isReadonly ? (
          <ProjectPageSidebarActions
            actions={actions}
            autoFocus={autoFocus}
            isIconOnly={!!formModel.formOptions?.hideProject}
          />
        ) : null}
      </section>
    )
  }
)
